import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Settings, Server, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export function AdminSettings() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(true);

  const handleSaveConfig = () => {
    toast.success('Platform configuration saved successfully.');
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium tracking-tight text-foreground flex items-center gap-2">
          <Settings className="h-6 w-6 text-muted-foreground" />
          Platform Settings
        </h1>
        <p className="text-sm text-muted-foreground">Global configurations for the DEW SaaS platform</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Core Platform Settings */}
        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Server className="h-4 w-4" /> Core Config
            </CardTitle>
            <CardDescription className="text-muted-foreground">Application-wide toggles</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="space-y-1">
                <Label className="text-foreground text-sm font-medium">Maintenance Mode</Label>
                <p className="text-xs text-muted-foreground">Blocks all non-admin logins</p>
              </div>
              <Switch checked={maintenanceMode} onCheckedChange={setMaintenanceMode} />
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <div className="space-y-1">
                <Label className="text-foreground text-sm font-medium">Open Registration</Label>
                <p className="text-xs text-muted-foreground">Allow new tenants to sign up</p>
              </div>
              <Switch checked={signupEnabled} onCheckedChange={setSignupEnabled} />
            </div>
            
            <Button onClick={handleSaveConfig} className="w-full bg-purple-600 hover:bg-purple-700 text-foreground">
              Save Core Settings
            </Button>
          </CardContent>
        </Card>

        {/* Email & SMTP */}
        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <Mail className="h-4 w-4" /> SMTP & Email
            </CardTitle>
            <CardDescription className="text-muted-foreground">System email delivery provider</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">From Address</Label>
              <Input defaultValue="noreply@dpldew.com" className="bg-slate-800 border-slate-700 text-foreground" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">SMTP Host</Label>
              <Input defaultValue="smtp.resend.com" className="bg-slate-800 border-slate-700 text-foreground" />
            </div>
            <Button variant="outline" className="w-full border-slate-700 text-slate-300 hover:text-foreground hover:bg-slate-800">
              Send Test Email
            </Button>
          </CardContent>
        </Card>

        {/* Master Admin Security */}
        <Card className="bg-slate-900 border-slate-800 shadow-sm lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg text-foreground flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Superadmin Roles
            </CardTitle>
            <CardDescription className="text-muted-foreground">Manage who has access to this dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="p-8 text-center bg-slate-800/30 rounded-xl border border-dashed border-slate-700/50">
              <p className="text-sm font-medium text-foreground mb-2">Role Management coming soon</p>
              <p className="text-xs text-muted-foreground max-w-md mx-auto">
                Currently, only users manually added to the <code className="text-purple-400 px-1 py-0.5 bg-purple-500/10 rounded">admin_users</code> table with Master Admin privileges can access this panel.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
